/**
 * @author      calvin@zumper.com (Calvin Yau)
 * @copyright   Copyright (c) 2019, Zumper
 * @description navigateToAutocompleteResult thunk
 */

import { updateUrlFromLocationFilter } from '@zcode-ui/filters'
import { TILE_SIZE, getBoxFromBounds, getCityBoxFromBounds } from '@zcode-ui/map'
import { selectIsMobile, selectListables } from '@zcode-ui/web-zumper-global-state'
import type { AutocompleteBuildingResult, AutocompleteResult } from '@zcode/bizos'
import {
  DEFAULT_MAP_LOCATION,
  ZOOM_CITY,
  ZOOM_CLUSTER_FILTER_NOT_APPLICABLE,
  getFittedZoom,
  keepBetaParam,
  locationDescriptorToString,
} from '@zcode/nr-utils'
import getLocationFromAutocompleteResult from '@zcode/nr/es/shared/components/Autocomplete/helpers/getLocationFromAutocompleteResult'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import { trackPageview } from '@zcode/nr/es/shared/utils/analytics'
import type { MaybeEmpty } from '@zcode/utility-types'
import { GET_LISTABLES_SUCCESS } from '../../../actions/constants'
import { default as loadCity } from '../../../actions/loadCity'
import { NAVBAR_MOBILE_LIST, NAVBAR_MOBILE_MAP } from '../../../constants/navbar'
import { logger } from '../../../logger'
import { synchronizeCurrentSearchLocation } from '../../currentSearch/modules/location/thunks/synchronizeCurrentSearchLocation'
import { loadGeos } from '../../geo/thunks/loadGeos'
import { setMobileViewType } from '../../navbar/actions/index'
import { selectMobileViewType } from '../../navbar/selectors/index'
import type { Dispatch, GetState } from '../../types'

const navigateToAutocompleteResult =
  ({
    result,
    height,
    width,
  }:
    | {
        result: AutocompleteResult
        height: number | null
        width: number | null
      }
    | { result: AutocompleteBuildingResult; height?: never; width?: never }) =>
  async (dispatch: Dispatch, getState: GetState) => {
    const state = getState()
    const isMobile = selectIsMobile(state)
    const { history } = dispatch(getRouteProps())

    // Here we reset isLowResults to false on navigate to different location
    const listables = selectListables(state)
    dispatch({
      type: GET_LISTABLES_SUCCESS,
      listables: { ...listables, isLowResults: false, maxBox: null },
    })

    try {
      if (result.type === 'building') {
        const urlToOpen = locationDescriptorToString(keepBetaParam(result.url), history)
        window.open(urlToOpen, '_blank')
      } else {
        const { locationObj } = await getLocationFromAutocompleteResult({
          hoodsLimit: 12,
          width,
          height,
          result,
        })
        if (locationObj) {
          const { isCityGeoType, isHoodGeoType, shouldOmitBox, url, zoom } = locationObj
          const { city, hood }: MaybeEmpty<Awaited<ReturnType<ReturnType<typeof loadGeos>>>> = url
            ? await dispatch(loadGeos(url))
            : {}

          if (isHoodGeoType && hood) {
            const calculatedHoodBox = getBoxFromBounds(hood)
            if (calculatedHoodBox) {
              locationObj.box = calculatedHoodBox
              locationObj.zoom =
                getFittedZoom({
                  bounds: calculatedHoodBox,
                  dimensions: { height, width },
                  tileSize: TILE_SIZE,
                  allowFloat: true,
                }) - 0.5
            }
          }

          if (isCityGeoType && url) {
            const cityObj: MaybeEmpty<Awaited<ReturnType<ReturnType<typeof loadCity>>>> =
              (await dispatch(loadCity(url))) || {}
            const cityBounds = cityObj?.displayBounds
            const cityCoordinates = cityBounds?.coordinates
            const calculatedCityBox = cityCoordinates
              ? getCityBoxFromBounds(cityCoordinates)
              : undefined
            if (calculatedCityBox) {
              locationObj.box = calculatedCityBox
              locationObj.zoom =
                getFittedZoom({
                  bounds: calculatedCityBox,
                  dimensions: { height, width },
                  tileSize: TILE_SIZE,
                  allowFloat: true,
                }) - 0.5
            }
            if (locationObj.zoom <= ZOOM_CLUSTER_FILTER_NOT_APPLICABLE) {
              locationObj.zoom = ZOOM_CLUSTER_FILTER_NOT_APPLICABLE + 0.1
            }
          }

          dispatch(
            synchronizeCurrentSearchLocation({
              location: locationObj,
              city,
              hood,
              ignoreLowResultsFlag: true,
              minZoom: zoom || ZOOM_CITY,
            })
          )
          dispatch(
            updateUrlFromLocationFilter({
              omitBox: shouldOmitBox,
              result,
            })
          )
        }
      }
      trackPageview()
      if (isMobile) {
        const mobileViewType = selectMobileViewType(state)
        mobileViewType === NAVBAR_MOBILE_MAP && dispatch(setMobileViewType(NAVBAR_MOBILE_LIST))
      }
    } catch (e) {
      logger.warn('Autocomplete result click encountered a problem', e)
      dispatch(
        synchronizeCurrentSearchLocation({
          ignoreLowResultsFlag: true,
          location: DEFAULT_MAP_LOCATION,
          minZoom: ZOOM_CITY,
        })
      )
      dispatch(updateUrlFromLocationFilter())
    }
  }

export default navigateToAutocompleteResult
